import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, Col, Form, Button, Alert, Tabs, Tab, Card } from 'react-bootstrap';
import { newsApi, categoriesApi, galleryApi } from '../services/api';
import { Category } from '../types';
import { generateUniqueSlug } from '../services/slugUtils';
import { getVideoEmbedUrl, getVideoPlatform } from '../services/videoUtils';
import '../styles/pages.css';

const NewsForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [activeTab, setActiveTab] = useState<string>('info');
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Form data
  const [formData, setFormData] = useState<any>({
    titulo: '',
    slug: '',
    subtitulo: null,
    texto: '',
    categoriaId: 1,
    dataNoticia: new Date().toISOString().split('T')[0],
    fonte: 'Agricampanha',
    videoUrl: '',
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Gallery data
  const [newsImages, setNewsImages] = useState<{ id?: number; url: string; isPrincipal?: boolean }[]>([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [galleryId, setGalleryId] = useState<number | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchCategories();
    if (isEditing) {
      fetchNews();
    }
  }, [id, navigate, isEditing]);

  const fetchCategories = async () => {
    try {
      const data = await categoriesApi.getActive();
      setCategories(data);
      if (data.length > 0 && !isEditing) {
        setFormData((prev: any) => ({ ...prev, categoriaId: data[0].id }));
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Erro ao carregar categorias');
    }
  };

  const fetchNews = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await newsApi.getById(parseInt(id));
      if (data) {
        setFormData({
          titulo: data.titulo,
          slug: data.slug || '',
          subtitulo: data.subtitulo || null,
          texto: data.texto,
          categoriaId: data.categoriaId,
          dataNoticia: data.dataNoticia.split('T')[0],
          fonte: data.fonte || 'Agricampanha',
          videoUrl: data.videoUrl || '',
        });
        setSlugManuallyEdited(true); // em edição, preserva o slug existente

        if (data.idGaleriaFotos) {
          setGalleryId(data.idGaleriaFotos);
        }

        if (data.galeriaFotos?.fotos?.length) {
          const sortedPhotos = [...data.galeriaFotos.fotos].sort((a, b) => a.ordem - b.ordem);
          setNewsImages(sortedPhotos.map(foto => ({ id: foto.id, url: foto.url, isPrincipal: foto.isPrincipal })));
        } else if (data.imagemUrl) {
          setNewsImages([{ url: data.imagemUrl, isPrincipal: true }]);
        }
      }
    } catch (err: any) {
      console.error('Error loading news:', err);
      setError('Erro ao carregar notícia');
      if (err.message?.includes('401') || err.message?.includes('expirada')) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddImage = () => {
    const url = newImageUrl.trim();
    if (!url) return;
    setNewsImages(prev => [...prev, { url }]);
    setNewImageUrl('');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingImage(true);
    setError('');
    try {
      const results = await Promise.all(files.map(f => newsApi.uploadImage(f)));
      setNewsImages(prev => {
        const novasFotos = results.map((r, i) => ({
          url: r.url,
          isPrincipal: prev.length === 0 && i === 0,
        }));
        return [...prev, ...novasFotos];
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao fazer upload');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = async (index: number) => {
    const photo = newsImages[index];
    if (!window.confirm('Remover esta foto?')) return;
    if (photo.id) {
      try {
        await galleryApi.deletePhoto(photo.url);
      } catch (err) {
        console.error('Erro ao deletar foto:', err);
      }
    }
    setNewsImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSetPrincipal = (index: number) => {
    setNewsImages(prev => prev.map((p, i) => ({ ...p, isPrincipal: i === index })));
  };

  const handleMoveImage = (index: number, direction: 'up' | 'down') => {
    const newImages = [...newsImages];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newImages.length) return;
    [newImages[index], newImages[swapIndex]] = [newImages[swapIndex], newImages[index]];
    setNewsImages(newImages);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.titulo?.trim()) {
      setError('O título é obrigatório');
      setActiveTab('info');
      return;
    }
    if (!formData.texto?.trim()) {
      setError('O conteúdo é obrigatório');
      setActiveTab('info');
      return;
    }
    if (!formData.dataNoticia) {
      setError('A data é obrigatória');
      setActiveTab('info');
      return;
    }

    setLoading(true);
    try {
      // Gera slug único se ainda não tem (criação nova)
      let slug = formData.slug;
      if (!slug) {
        slug = await generateUniqueSlug(formData.titulo, newsApi.slugExists);
      }

      const principalPhoto = newsImages.find(p => p.isPrincipal) || newsImages[0];
      const newsData: any = {
        titulo: formData.titulo,
        slug,
        subtitulo: formData.subtitulo || '',
        texto: formData.texto,
        categoriaId: formData.categoriaId,
        dataNoticia: formData.dataNoticia,
        fonte: formData.fonte || 'Agricampanha',
        imagemUrl: principalPhoto?.url || undefined,
        videoUrl: formData.videoUrl || undefined,
      };

      let newsId: number;
      let currentGalleryId = galleryId;

      if (isEditing) {
        await newsApi.update(parseInt(id!), newsData);
        newsId = parseInt(id!);
      } else {
        const result = await newsApi.create(newsData);
        newsId = result.id;
        currentGalleryId = result.idGaleriaFotos ?? null;
        setGalleryId(currentGalleryId);
      }

      // Salva fotos na galeria
      if (newsImages.length > 0) {
        if (!currentGalleryId) {
          const newGallery = await galleryApi.create({ titulo: formData.titulo });
          currentGalleryId = newGallery.id;
          setGalleryId(currentGalleryId);
          await newsApi.update(newsId, { idGaleriaFotos: currentGalleryId } as any);
        }

        for (let i = 0; i < newsImages.length; i++) {
          const photo = newsImages[i];
          const ordem = i + 1;
          const isPrincipal = photo.isPrincipal ?? false;

          if (photo.id) {
            await galleryApi.updatePhoto(photo.id, { ordem, isPrincipal });
          } else {
            const saved = await galleryApi.addPhoto(currentGalleryId!, {
              url: photo.url,
              legenda: '',
              ordem,
              isPrincipal,
            });
            setNewsImages(prev =>
              prev.map((p, idx) => idx === i ? { ...p, id: saved.id } : p)
            );
          }
        }
      }

      setSuccess(isEditing
        ? 'Notícia atualizada com sucesso! Você pode voltar para a listagem.'
        : 'Notícia criada com sucesso!');

      if (!isEditing) {
        setTimeout(() => navigate('/admin'), 1500);
      }
    } catch (err: any) {
      console.error('❌ ERRO ao salvar notícia:', err);
      let errorMessage = err.message || 'Erro desconhecido ao salvar notícia';
      if (errorMessage.includes('expirada') || errorMessage.includes('401') || errorMessage.includes('Sessão')) {
        errorMessage = 'Sua sessão expirou. Faça login novamente.';
        setError(errorMessage);
        localStorage.removeItem('admin_token');
        setTimeout(() => navigate('/admin/login'), 2000);
        return;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const saveButton = (
    <Button variant="primary" onClick={handleSubmit} disabled={loading}>
      {loading ? (
        <>
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Salvando...
        </>
      ) : (
        <>
          <i className="bi bi-check-lg me-2"></i>
          {isEditing ? 'Atualizar' : 'Criar'}
        </>
      )}
    </Button>
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="bi bi-newspaper me-2"></i>
          {isEditing ? 'Editar Notícia' : 'Nova Notícia'}
        </h2>
      </div>

      <div>
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            <div className="d-flex justify-content-between align-items-center">
              <span>{success}</span>
              {isEditing && (
                <Button variant="success" size="sm" onClick={() => navigate('/admin')}>
                  <i className="bi bi-arrow-left me-2"></i>
                  Voltar para listagem
                </Button>
              )}
            </div>
          </Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card>
          <Card.Body>
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'info')} className="mb-4">
              <Tab eventKey="info" title={<><i className="bi bi-info-circle me-2"></i>Informações</>}>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Título *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.titulo}
                      onChange={(e) => {
                        const titulo = e.target.value;
                        setFormData((prev: any) => ({
                          ...prev,
                          titulo,
                          // Auto-preenche slug enquanto não editado manualmente (só em criação)
                          slug: (!isEditing && !slugManuallyEdited)
                            ? titulo.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[°º]/g, 'o').replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 100)
                            : prev.slug,
                        }));
                      }}
                      required
                      placeholder="Digite o título da notícia"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Slug (URL) {isEditing && <small className="text-muted ms-1">— não altere após publicar</small>}
                    </Form.Label>
                    <div className="input-group">
                      <span className="input-group-text text-muted" style={{ fontSize: '0.85rem' }}>/noticias/</span>
                      <Form.Control
                        type="text"
                        value={formData.slug}
                        onChange={(e) => {
                          setSlugManuallyEdited(true);
                          setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') });
                        }}
                        placeholder="gerado-automaticamente-do-titulo"
                      />
                    </div>
                    <Form.Text className="text-muted">Gerado automaticamente do título. Só altere se necessário.</Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Subtítulo</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.subtitulo || ''}
                      onChange={(e) => setFormData({ ...formData, subtitulo: e.target.value || null })}
                      placeholder="Digite o subtítulo (opcional)"
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Categoria *</Form.Label>
                        <Form.Select
                          value={formData.categoriaId}
                          onChange={(e) => setFormData({ ...formData, categoriaId: parseInt(e.target.value) })}
                          required
                        >
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.nome}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Data *</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.dataNoticia}
                          onChange={(e) => setFormData({ ...formData, dataNoticia: e.target.value })}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Fonte</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.fonte}
                      onChange={(e) => setFormData({ ...formData, fonte: e.target.value })}
                      placeholder="Digite a fonte da notícia"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Conteúdo *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={15}
                      value={formData.texto}
                      onChange={(e) => setFormData({ ...formData, texto: e.target.value })}
                      required
                      placeholder="Digite o conteúdo completo da notícia"
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-end gap-2">
                    <Button variant="secondary" onClick={() => navigate('/admin')}>Cancelar</Button>
                    {saveButton}
                  </div>
                </Form>
              </Tab>

              <Tab eventKey="fotos" title={<><i className="bi bi-images me-2"></i>Mídia ({newsImages.length})</>}>
                <div className="mb-4">
                  <h5>Vídeo</h5>
                  <Form.Group className="mb-4">
                    <Form.Label>
                      <i className="bi bi-play-circle me-2"></i>
                      URL do vídeo ou post (YouTube, Instagram ou Vimeo)
                    </Form.Label>
                    <Form.Control
                      type="url"
                      value={formData.videoUrl}
                      onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                      placeholder="https://www.youtube.com/watch?v=... ou https://www.instagram.com/reel/..."
                    />
                    <Form.Text className="text-muted">
                      Cole o link do YouTube, Instagram (post ou reel) ou Vimeo. Aparece no topo da notícia.
                    </Form.Text>
                    {formData.videoUrl && (() => {
                      const embedUrl = getVideoEmbedUrl(formData.videoUrl);
                      const platform = getVideoPlatform(formData.videoUrl);
                      if (embedUrl) {
                        return (
                          <div className="mt-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <i className={`bi bi-${platform === 'youtube' ? 'youtube' : platform === 'instagram' ? 'instagram' : 'camera-video'} text-danger`}></i>
                              <small className="text-success fw-semibold">
                                {platform === 'youtube' ? 'YouTube' : platform === 'instagram' ? 'Instagram' : 'Vimeo'} detectado ✓
                              </small>
                            </div>
                            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: '8px', overflow: 'hidden', background: '#000' }}>
                              <iframe
                                src={embedUrl}
                                title="Preview do vídeo"
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div className="mt-2">
                          <small className="text-warning">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            URL não reconhecida. Use links do YouTube ou Vimeo.
                          </small>
                        </div>
                      );
                    })()}
                  </Form.Group>

                  <hr className="mb-4" />

                  <h5>Galeria de Fotos</h5>
                  <p className="text-muted">
                    Faça upload das fotos ou insira a URL. Use a estrela para definir a capa. As setas reordenam.
                  </p>

                  <Row className="mb-4 g-3">
                    <Col md={6}>
                      <Form.Label>Upload de arquivos</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && (
                        <div className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Enviando...
                        </div>
                      )}
                    </Col>
                    <Col md={6}>
                      <Form.Label>Ou insira a URL</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          placeholder="https://... ou /img/..."
                          value={newImageUrl}
                          onChange={(e) => setNewImageUrl(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddImage(); } }}
                        />
                        <Button variant="outline-primary" onClick={handleAddImage}>
                          <i className="bi bi-plus-lg"></i>
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {newsImages.length === 0 ? (
                    <div className="text-center py-5" style={{ background: 'var(--color-gray-light)', borderRadius: '8px' }}>
                      <i className="bi bi-images" style={{ fontSize: '3rem', color: 'var(--color-gray)' }}></i>
                      <p className="text-muted mt-3">Nenhuma foto adicionada ainda</p>
                    </div>
                  ) : (
                    <Row className="g-3">
                      {newsImages.map((photo, index) => (
                        <Col key={index} md={4} sm={6}>
                          <Card>
                            <div
                              style={{
                                height: '180px',
                                backgroundImage: `url('${photo.url}')`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundColor: '#f0f0f0',
                              }}
                            />
                            <Card.Body className="p-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-1">
                                  <Button
                                    variant={photo.isPrincipal ? 'warning' : 'outline-secondary'}
                                    size="sm"
                                    title="Definir como capa"
                                    onClick={() => handleSetPrincipal(index)}
                                  >
                                    <i className={`bi bi-star${photo.isPrincipal ? '-fill' : ''}`}></i>
                                  </Button>
                                  <div className="d-flex gap-1 ms-1">
                                    <Button variant="outline-secondary" size="sm" onClick={() => handleMoveImage(index, 'up')} disabled={index === 0}>
                                      <i className="bi bi-arrow-up"></i>
                                    </Button>
                                    <Button variant="outline-secondary" size="sm" onClick={() => handleMoveImage(index, 'down')} disabled={index === newsImages.length - 1}>
                                      <i className="bi bi-arrow-down"></i>
                                    </Button>
                                  </div>
                                </div>
                                <Button variant="outline-danger" size="sm" onClick={() => handleRemoveImage(index)}>
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}

                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button variant="secondary" onClick={() => navigate('/admin')}>Cancelar</Button>
                    {saveButton}
                  </div>
                </div>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default NewsForm;
